import React, { useEffect, useRef, useState } from "react";
import RightworkDatePicker from "../../../components/shared/RightworkDatePicker";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BreakDetails from "./BreakDetails";
import FlagReasonsTooltip from "./FlagReasonsTooltip";
import TimecardHistoryTooltip from "./TimecardHistoryTooltip";
import { formatBreakTime } from "./timecardBreakUtils";
import { formatDateReadable, formatDateReadableWithDayOfWeekNoYear } from "../requests/utilities/helpers";
import { getRightworkAPI } from "../requests/utilities/requests";
import { API_get_timecards_for_store } from "../../../constants";

export function rangeError(start, end) {
  const first = Date.parse(`${start}T00:00:00Z`);
  const last = Date.parse(`${end}T00:00:00Z`);
  return !Number.isFinite(first) || !Number.isFinite(last) || last < first || (last - first) / 86400000 >= 93
    ? "Choose a start and end date spanning at most 93 days." : "";
}

// Same labels and colors as the time entries table's status badges.
const STATUS_BADGES = {
  auto_approved: { label: "Auto-Approved", classes: "bg-emerald-50 border border-emerald-300 text-emerald-800" },
  auto_flag: { label: "Flagged", classes: "bg-fuchsia-50 border border-fuchsia-300 text-fuchsia-800" },
  approved: { label: "Approved", classes: "bg-blue-50 border border-blue-300 text-blue-800" },
  denied: { label: "Denied", classes: "bg-rose-50 border border-rose-300 text-red-800" },
  pending: { label: "Pending", classes: "bg-yellow-50 border border-yellow-300 text-yellow-800" },
  currently_working: { label: "In Progress", classes: "bg-violet-50 border border-violet-300 text-violet-800" },
  Imported: { label: "Imported", classes: "bg-slate-50 border border-slate-300 text-slate-700" },
};
const normalizeStatus = status => ({ manual_approved: "approved", manual_denied: "denied" }[status] || status);
const missing = <span className="text-slate-400">–</span>;

// The report loads every page of the range (each request stays bounded at the
// API's page size) so the whole range sorts like the time entries table. The
// cap keeps a very large range from flooding the browser or the database.
export const MAX_REPORT_ENTRIES = 3000;

// Same button shape as the Tips page; 42px to sit level with the date pickers.
const btnPrimary = "flex h-[42px] items-center gap-2 rounded-lg bg-violet-700 px-4 text-sm font-medium text-white hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50";
const thClass = "px-3 py-3 text-left text-xs font-bold uppercase text-slate-500";

// Same columns and sort behavior as the time entries table. Dept only shows
// when the company has more than one department, like the time entries table.
const columnsFor = (showDepartment) => [
  { key: "name", label: "Name", className: "py-3 pl-5 pr-3 text-left text-xs font-bold uppercase text-slate-500" },
  ...(showDepartment ? [{ key: "department", label: "Dept", className: thClass }] : []),
  { key: "job", label: "Job", className: thClass },
  { key: "date", label: "Date", className: thClass },
  { key: "timesheet", label: "Timesheet", className: thClass },
  { key: "paid_hours", label: "Hours Paid", className: thClass },
  { key: "unpaid_breaks", label: "Unpaid Breaks", className: thClass },
  { key: "paid_breaks", label: "Paid Breaks", className: thClass },
  { key: "status", label: "Status", className: thClass },
];

// Job is the payroll role (resolved backend-side like the Paylocity export);
// Dept is that role's department from the store's role list.
const jobOf = (entry) => entry.payroll_role?.role_name || null;
const departmentOf = (entry, rolesList) =>
  entry.payroll_role ? rolesList.find((role) => role.id === entry.payroll_role.id)?.role_type || null : null;

export function sortValue(entry, column, rolesList = []) {
  switch (column) {
    case "name":
      return (entry.employee || "").toLowerCase();
    case "job":
      return (jobOf(entry) || "").toLowerCase();
    case "department":
      return (departmentOf(entry, rolesList) || "").toLowerCase();
    case "date":
    case "timesheet":
      // Chronological by clock-in; imported entries only carry a date.
      if (entry.clocked_in_at) return new Date(entry.clocked_in_at).getTime();
      return entry.date ? new Date(entry.date).getTime() : 0;
    case "paid_hours":
      return Number(entry.paid_hours ?? 0);
    case "unpaid_breaks":
      return Number(entry.break_minutes ?? 0);
    case "paid_breaks":
      return Number(entry.paid_break_minutes ?? 0);
    case "status":
      return STATUS_BADGES[normalizeStatus(entry.status)]?.label || "";
    default:
      return 0;
  }
}

export function sortEntries(entries, sort, rolesList = []) {
  if (!sort.column) return entries;
  return [...entries].sort((a, b) => {
    const valueA = sortValue(a, sort.column, rolesList);
    const valueB = sortValue(b, sort.column, rolesList);
    const cmp = typeof valueA === "string" ? valueA.localeCompare(valueB) : valueA - valueB;
    return sort.direction === "asc" ? cmp : -cmp;
  });
}
const tdClass = "whitespace-nowrap px-3 py-5 text-sm text-slate-900";

function StatusBadge({ status, flagReason }) {
  const badge = STATUS_BADGES[normalizeStatus(status)];
  if (!badge) return missing;
  const flagged = status === "auto_flag" && flagReason;
  const pill = (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium whitespace-nowrap ${flagged ? "cursor-pointer " : ""}${badge.classes}`}>
      {status === "auto_flag" && <WarningIcon style={{ fontSize: 16 }} />}
      {badge.label}
    </div>
  );
  return flagged ? <FlagReasonsTooltip flagReason={flagReason}>{pill}</FlagReasonsTooltip> : pill;
}

// Same cell formats as the time entries table: "06:44 PM - 06:45 PM",
// "6.80hrs", "30min", "–" for a missing value.
function timesheet(entry, timeZone) {
  if (entry.local_clock_in) return `${entry.local_clock_in} - ${entry.local_clock_out || ""}`;
  if (!entry.clocked_in_at) return missing;
  return `${formatBreakTime(entry.clocked_in_at, timeZone)} - ${formatBreakTime(entry.clocked_out_at, timeZone)}`;
}

// Rendered below TimeEntriesHeader, which provides the back navigation.
export default function TimecardRangeReport({ storeId, storeName, hoursRoundedToTenths = false, rolesList = [] }) {
  const hours = value => value == null ? missing : `${Number(value).toFixed(hoursRoundedToTenths ? 1 : 2)}hrs`;
  const minutes = value => value == null ? missing : `${Math.floor(Number(value))}min`;
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadedCount, setLoadedCount] = useState(0);
  // Oldest first by date (clock-in time breaks ties within a day).
  const [sort, setSort] = useState({ column: "date", direction: "asc" });
  const showDepartment = new Set(rolesList.map((role) => role.role_type).filter(Boolean)).size > 1;
  const columns = columnsFor(showDepartment);
  const requestId = useRef(0);
  useEffect(() => () => { requestId.current += 1; }, []);

  function changeDate(setter, value) {
    requestId.current += 1;
    setter(value);
    setReport(null);
    setError("");
    setLoading(false);
  }

  function toggleSort(column) {
    setSort(previous => previous.column === column
      ? { column, direction: previous.direction === "asc" ? "desc" : "asc" }
      : { column, direction: "asc" });
  }

  async function load() {
    const invalid = rangeError(start, end);
    if (invalid) { setError(invalid); return; }
    const id = ++requestId.current;
    setLoading(true);
    setLoadedCount(0);
    setError("");
    setReport(null);
    try {
      let meta = null;
      let entries = [];
      let cursor = null;
      do {
        const response = await getRightworkAPI(API_get_timecards_for_store, {
          store_id: storeId, start_date: start, end_date: end,
          ...(cursor ? { cursor } : {}),
        });
        if (id !== requestId.current) return;
        const data = response.data?.redux?.payload;
        if (data?.read_only !== true || !Array.isArray(data.entries)) throw new Error("Invalid report response");
        meta = meta || data;
        entries = entries.concat(data.entries);
        cursor = data.next_cursor;
        setLoadedCount(entries.length);
      } while (cursor && entries.length < MAX_REPORT_ENTRIES);
      setReport({ ...meta, entries: entries.slice(0, MAX_REPORT_ENTRIES), truncated: Boolean(cursor) || entries.length > MAX_REPORT_ENTRIES });
    } catch (failure) {
      if (id === requestId.current) setError(failure.response?.data?.system?.message || "Unable to load the report. Please try again.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }

  return (
    <section className="px-6 mt-8 pb-8" aria-label="Custom date range">
      <h1 className="text-xl font-bold text-slate-900">Custom Date Range</h1>
      <h3 className="mt-1 text-slate-500">{storeName} · Read-only. Make edits and approvals in each pay period.</h3>
      <form className="mt-6 flex flex-wrap items-center gap-3" onSubmit={event => { event.preventDefault(); load(); }}>
        <RightworkDatePicker width={170} label="Start date" value={start} onChange={value => changeDate(setStart, value)} />
        <span className="text-sm text-slate-500">to</span>
        <RightworkDatePicker width={170} label="End date" value={end} onChange={value => changeDate(setEnd, value)} />
        <button type="submit" className={btnPrimary} disabled={loading}>View report</button>
      </form>
      <p className="mt-2 text-xs text-slate-500">Up to 93 days. Entries are included by their clock-in date in the store timezone; imported entries use their own date.</p>
      {loading && <p role="status" className="mt-6 text-sm text-slate-500">Loading time entries…{loadedCount > 0 ? ` ${loadedCount.toLocaleString()} so far` : ""}</p>}
      {error && <p role="alert" className="mt-4 text-sm text-red-700">{error}</p>}
      {report && <div className="mt-6">
        <p className="mb-3 text-sm text-slate-500">{formatDateReadable(report.start_date)} to {formatDateReadable(report.end_date)} · {report.store_timezone} · {report.entries.length.toLocaleString()} {report.entries.length === 1 ? "entry" : "entries"}</p>
        {report.truncated && (
          <p role="alert" className="mb-3 text-sm text-amber-700">Showing the first {MAX_REPORT_ENTRIES.toLocaleString()} entries. Choose a shorter range to see them all.</p>
        )}
        {report.entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-6 text-sm text-slate-500">
            No time entries found for this range.
          </div>
        ) : (
          <div className="min-w-full overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.key}
                      scope="col"
                      aria-sort={sort.column === col.key ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
                      className={`${col.className} cursor-pointer select-none hover:text-slate-700`}
                      onClick={() => toggleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sort.column === col.key && (sort.direction === "asc"
                          ? <ArrowUpwardIcon style={{ fontSize: "14px" }} />
                          : <ArrowDownwardIcon style={{ fontSize: "14px" }} />)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">{sortEntries(report.entries, sort, rolesList).map(entry => (
                <tr key={entry.key}>
                  <td className="whitespace-nowrap py-5 pl-5 pr-3 text-sm text-slate-900">{entry.employee}</td>
                  {showDepartment && <td className={tdClass}>{departmentOf(entry, rolesList) ?? missing}</td>}
                  <td className={tdClass}>{jobOf(entry) ?? missing}</td>
                  <td className={tdClass}>{entry.date ? formatDateReadableWithDayOfWeekNoYear(entry.date) : missing}</td>
                  <td className={tdClass}>
                    <div className="flex items-center gap-2">
                      <span>{timesheet(entry, report.store_timezone)}</span>
                      {entry.history?.length > 0 && <TimecardHistoryTooltip history={entry.history} timecardId={entry.key} rolesList={rolesList} timeZone={report.store_timezone} />}
                    </div>
                  </td>
                  <td className={tdClass}>{hours(entry.paid_hours)}</td>
                  <td className={tdClass}><div>{minutes(entry.break_minutes)}</div><BreakDetails entry={entry} paid={false} timeZone={report.store_timezone} /></td>
                  <td className={tdClass}><div>{minutes(entry.paid_break_minutes)}</div><BreakDetails entry={entry} paid={true} timeZone={report.store_timezone} /></td>
                  <td className={tdClass}><StatusBadge status={entry.status} flagReason={entry.flag_reason} /></td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-xs text-slate-500">A dash means the value is missing. This report does not calculate payroll totals.</p>
      </div>}
    </section>
  );
}
